import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import DOMPurify from "dompurify";
import SEO from "../components/SEO";
import NewsletterSignup from "../components/NewsletterSignup";
import { createArticleSchema } from "../seo/schemas/articleSchema";
import logo from "../assets/logo_basic.png";
import AppFooter from "../components/AppFooter";
import PublicSiteHeader from "../components/PublicSiteHeader";
import { rememberBlogAttribution, trackEvent } from "../utils/blogAnalytics";

export default function BlogDetailPage() {
  const { slug } = useParams();

  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [session, setSession] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  
const [liked, setLiked] = useState(false);
const [saved, setSaved] = useState(false);
const [likeCount, setLikeCount] = useState(0);
const [actionMessage, setActionMessage] = useState("");
const [authPopup, setAuthPopup] = useState(false);
  useEffect(() => {
    loadSession();
    loadPost();
  }, [slug]);

  const loadSession = async () => {
    const { data } = await supabase.auth.getSession();
    setSession(data?.session || null);
  };

  const loadPost = async () => {
    setLoading(true);

    const { data, error } = await supabase
  .from("blog_posts")
  .select(`
    *,
    blog_categories (
      id,
      name,
      slug
    ),
    authors (
      id,
      name,
      title,
      bio,
      linkedin,
      website,
      photo
    )
  `)
  .eq("slug", slug)
  .eq("status", "published")
  .maybeSingle();
    if (error || !data) {
      console.error("Blog detail error:", error);
      setPost(null);
      setRelatedPosts([]);
      setLoading(false);
      return;
    }

    setPost(data);

    const attribution = {
      blogPostId: data.id,
      blogSlug: data.slug,
      categorySlug: data.blog_categories?.slug || data.category_slug || "",
      primaryKeyword: data.primary_keyword || "",
    };

    rememberBlogAttribution(attribution);
    trackEvent("blog_view", attribution);

    loadComments(data.id);
    loadRelatedPosts(data.id, data.category_id);
    loadArticleActions(data.id);
    setLoading(false);
  };

  const loadRelatedPosts = async (postId, categoryId) => {
    if (!categoryId) {
      setRelatedPosts([]);
      return;
    }

    const { data, error } = await supabase
      .from("blog_posts")
      .select(
        `
        *,
        blog_categories (
          id,
          name,
          slug
        )
      `
      )
      .eq("status", "published")
      .eq("category_id", categoryId)
      .neq("id", postId)
      .order("published_at", { ascending: false })
      .limit(3);

    if (error) {
      console.error("Related posts error:", error);
      setRelatedPosts([]);
      return;
    }

    setRelatedPosts(data || []);
  };
const goToCommentRegister = () => {
  const returnPath = `/blog/${post.slug}`;

  localStorage.setItem("redirect_after_login", returnPath);
  localStorage.setItem("comment_register_return", returnPath);
  localStorage.setItem("selectedPlan", "free_calculator");

  navigate(
    `/register?plan=free_calculator&source=comment&return=${encodeURIComponent(returnPath)}`
  );
};
  const loadComments = async (postId) => {
    const { data, error } = await supabase
      .from("blog_comments")
      .select("*")
      .eq("post_id", postId)
      .eq("approved", true)
      .order("created_at", { ascending: false });

    if (!error) setComments(data || []);
  };
const loadArticleActions = async (postId) => {
  const { count, error: countError } = await supabase
    .from("blog_likes")
    .select("*", { count: "exact", head: true })
    .eq("post_id", postId);

  if (!countError) setLikeCount(count || 0);

  const { data: sessionData } = await supabase.auth.getSession();
  const user = sessionData?.session?.user;

  if (!user) {
    setLiked(false);
    setSaved(false);
    return;
  }

  const { data: likeData } = await supabase
    .from("blog_likes")
    .select("id")
    .eq("post_id", postId)
    .eq("user_id", user.id)
    .maybeSingle();

  setLiked(!!likeData);

  const { data: saveData } = await supabase
    .from("saved_articles")
    .select("id")
    .eq("post_id", postId)
    .eq("user_id", user.id)
    .maybeSingle();

  setSaved(!!saveData);
};

const toggleLike = async () => {
  setActionMessage("");

  if (!session?.user) {
  setAuthPopup(true);
  return;
}
  if (!post?.id) return;

  if (liked) {
    const { error } = await supabase
      .from("blog_likes")
      .delete()
      .eq("post_id", post.id)
      .eq("user_id", session.user.id);

    if (error) {
      console.error("Unlike error:", error);
      setActionMessage(error.message);
      return;
    }

    setLiked(false);
    setLikeCount((x) => Math.max(0, x - 1));
  } else {
    const { error } = await supabase.from("blog_likes").insert({
      post_id: post.id,
      user_id: session.user.id,
    });

    if (error) {
      console.error("Like error:", error);
      setActionMessage(error.message);
      return;
    }

    setLiked(true);
    setLikeCount((x) => x + 1);
  }
};

const toggleSave = async () => {
  setActionMessage("");

  if (!session?.user) {
  setAuthPopup(true);
  return;
}

  if (!post?.id) return;

  if (saved) {
    const { error } = await supabase
      .from("saved_articles")
      .delete()
      .eq("post_id", post.id)
      .eq("user_id", session.user.id);

    if (error) {
      console.error("Unsave error:", error);
      setActionMessage(error.message);
      return;
    }

    setSaved(false);
    setActionMessage("Article removed from saved list.");
  } else {
    const { error } = await supabase.from("saved_articles").insert({
      post_id: post.id,
      user_id: session.user.id,
    });

    if (error) {
      console.error("Save error:", error);
      setActionMessage(error.message);
      return;
    }

    setSaved(true);
    setActionMessage("Article saved to your dashboard.");
  }
};
  const submitComment = async () => {
    setMessage("");

    if (!session?.user) {
      setMessage("Please login to comment.");
      return;
    }

    if (!comment.trim()) {
      setMessage("Please write a comment.");
      return;
    }

    const user = session.user;

    const { error } = await supabase.from("blog_comments").insert({
      post_id: post.id,
      user_id: user.id,
      user_name:
        user.user_metadata?.full_name || user.email || "Registered User",
      comment: comment.trim(),
  approved: false,
  rejected: false,
    });

    if (error) {
  console.error("Comment insert error:", error);
  setMessage(error.message || "Unable to submit comment.");
  return;
}

    setComment("");
    setMessage("Comment submitted successfully. It will appear after approval.");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0A24] text-white flex items-center justify-center">
        Loading article...
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0F0A24] text-white flex items-center justify-center">
        Article not found or not published.
      </div>
    );
  }

  const articleSchema = createArticleSchema({
    title:
      post.seo_title ||
      post.title,

    description:
      post.seo_description ||
      post.meta_description ||
      post.summary ||
      post.excerpt ||
      "Career intelligence article from ReigniteSpirit.",

    slug: post.slug,

    image:
      post.og_image ||
      post.featured_image ||
      post.cover_image_url ||
      "/og-blog.jpeg",

    authorName:
      post.authors?.name ||
      post.author_name ||
      "ReigniteSpirit Editorial Team",

    authorUrl:
      post.authors?.website ||
      post.authors?.linkedin ||
      "",

    datePublished:
      post.published_at ||
      post.created_at,

    dateModified:
      post.updated_at ||
      post.published_at ||
      post.created_at,

    category:
      post.blog_categories?.name ||
      post.category ||
      "Career Strategy",

    keywords:
      post.primary_keyword
        ? [post.primary_keyword]
        : post.keywords ||
          [
            post.title,
            post.blog_categories?.name || "Career Strategy",
            "Career Intelligence",
            "AI Careers",
            "ReigniteSpirit",
          ].filter(Boolean),
  });
const readingTime = post?.content
  ? Math.max(1, Math.ceil(post.content.trim().split(/\s+/).length / 200))
  : 1;

const publishedDate = post?.published_at || post?.created_at
  ? new Date(post.published_at || post.created_at).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    })
  : "";

const categorySlug = String(
  post?.blog_categories?.slug ||
  post?.category_slug ||
  ""
)
  .trim()
  .toLowerCase();

const relevantProduct =
  categorySlug === "financial-runway" ||
  categorySlug === "financial-planning" ||
  categorySlug === "career-finance"
    ? {
        path: "/runway-calculator",
        title: "Financial Runway Calculator",
        description:
          "Estimate how long your savings and other financial resources may support a planned career transition.",
      }
    : categorySlug === "career-assessment" ||
        categorySlug === "career-risk" ||
        categorySlug === "ai-career-risk"
      ? {
          path: "/snapshot",
          title: "Snapshot Career Report",
          description:
            "Get a focused career-risk and transition assessment based on your current professional situation.",
        }
      : {
          path: "/deep-audit",
          title: "Deep Career Audit",
          description:
            "Turn career intelligence into a detailed and personalized career transition roadmap.",
        };


  const trackBlogCta = (ctaName) => {
    if (!post?.id || !post?.slug) return;

    const attribution = {
      blogPostId: post.id,
      blogSlug: post.slug,
      categorySlug: post.blog_categories?.slug || post.category_slug || "",
      primaryKeyword: post.primary_keyword || "",
      ctaName,
    };

    rememberBlogAttribution(attribution);
    trackEvent("blog_cta_click", attribution);
  };

  const sanitizedContent = DOMPurify.sanitize(
    post?.content || "",
    {
      USE_PROFILES: {
        html: true,
      },
    }
  );


  return (
    <div className="min-h-screen bg-[#0F0A24] text-white">
      <PublicSiteHeader active="blog" />
      <main className="px-6 py-14">
            <SEO
        title={
          post.seo_title ||
          post.title
        }
        description={
          post.seo_description ||
          post.meta_description ||
          post.summary ||
          post.excerpt ||
          "Career intelligence article from ReigniteSpirit."
        }
        path={`/blog/${post.slug}`}
        image={
          post.og_image ||
          post.featured_image ||
          post.cover_image_url ||
          "/og-blog.jpeg"
        }
        type="article"
        author={
          post.authors?.name ||
          post.author_name ||
          "ReigniteSpirit Editorial Team"
        }
        publishedTime={
          post.published_at ||
          post.created_at
        }
        modifiedTime={
          post.updated_at ||
          post.published_at ||
          post.created_at
        }
        articleSection={
          post.blog_categories?.name ||
          post.category ||
          "Career Strategy"
        }
        keywords={
          post.primary_keyword ||
          (
            Array.isArray(post.keywords)
              ? post.keywords.join(", ")
              : post.keywords ||
                [
                  post.title,
                  post.blog_categories?.name || "Career Strategy",
                  "Career Intelligence",
                  "AI Careers",
                  "ReigniteSpirit",
                ]
                  .filter(Boolean)
                  .join(", ")
          )
        }
        schema={articleSchema}
      />
      <article className="max-w-4xl mx-auto">
        <Link to="/blog" className="text-[#EB6AB5] underline">
          ← Back to Blog
        </Link>

        <p className="text-[#EB6AB5] text-sm font-bold uppercase tracking-widest mt-8">
          {post.blog_categories?.name || "Career Strategy"}
        </p>

        <h1 className="text-4xl md:text-6xl font-extrabold mt-4 mb-6">
          {post.title}
        </h1>

        <p className="text-white/60 mb-8">
  By {post.authors?.name || post.author_name || "Reignite Spirit Team"} · {readingTime} min read
  {publishedDate ? ` · ${publishedDate}` : ""}
</p>
<div className="flex flex-wrap gap-3 mb-8">
  <button
    type="button"
    onClick={toggleLike}
    className={`px-5 py-2 rounded-full border ${
      liked
        ? "bg-[#EB6AB5] text-[#0F0A24] border-[#EB6AB5]"
        : "bg-white/5 text-white border-white/10"
    }`}
  >
    ❤️ {liked ? "Liked" : "Like"} ({likeCount})
  </button>

  <button
    type="button"
    onClick={toggleSave}
    className={`px-5 py-2 rounded-full border ${
      saved
        ? "bg-[#EB6AB5] text-[#0F0A24] border-[#EB6AB5]"
        : "bg-white/5 text-white border-white/10"
    }`}
  >
    💾 {saved ? "Saved" : "Save Article"}
  </button>
</div>

{/* {actionMessage && (
  <p className="text-sm text-[#EB6AB5] mb-6">{actionMessage}</p>
)} */}
        {post.cover_image_url && (
          <img
            src={post.cover_image_url}
            alt={post.title}
            className="rounded-3xl mb-10 w-full"
          />
        )}

       <div
  className="
    prose
    prose-invert
    prose-lg
    max-w-none
    text-white/85
    leading-8
    prose-headings:text-white
    prose-h2:text-3xl
    prose-h2:font-bold
    prose-h2:mt-10
    prose-h2:mb-5
    prose-h3:text-2xl
    prose-h3:font-bold
    prose-h3:mt-8
    prose-h3:mb-4
    prose-p:text-white/80
    prose-p:leading-8
    prose-a:text-[#EB6AB5]
    prose-a:underline
    prose-strong:text-white
    prose-ul:text-white/80
    prose-ol:text-white/80
    prose-li:my-2
    prose-blockquote:border-[#EB6AB5]
    prose-blockquote:text-white/70
  "
  dangerouslySetInnerHTML={{
    __html: sanitizedContent,
  }}
/>

{/* ================= Career Planning Resources ================= */}
<section
  className="mt-12 rounded-3xl border border-white/10 bg-white/5 p-7"
  aria-labelledby="article-related-resources"
>
  <h2
    id="article-related-resources"
    className="text-2xl font-bold text-white mb-5"
  >
    Continue your career planning
  </h2>

  <div className="grid sm:grid-cols-2 gap-4">
    <Link
      to={relevantProduct.path}
      onClick={() => trackBlogCta(`recommended_${categorySlug || "general"}`)}
      className="block rounded-xl border border-white/10 p-5 hover:border-[#EB6AB5] transition"
    >
      <p className="text-xs font-bold uppercase tracking-widest text-[#EB6AB5]">
        Recommended for this article
      </p>

      <h3 className="font-bold text-white mt-2">
        {relevantProduct.title}
      </h3>

      <p className="text-sm text-white/60 mt-2 leading-relaxed">
        {relevantProduct.description}
      </p>
    </Link>

    <Link
      to="/methodology"
      className="block rounded-xl border border-white/10 p-5 hover:border-[#EB6AB5] transition"
    >
      <h3 className="font-bold text-white">
        Our Methodology
      </h3>

      <p className="text-sm text-white/60 mt-2 leading-relaxed">
        Review how career risks, skills and pathways are evaluated.
      </p>
    </Link>

    <Link
      to="/data-sources"
      className="block rounded-xl border border-white/10 p-5 hover:border-[#EB6AB5] transition"
    >
      <h3 className="font-bold text-white">
        Data Sources
      </h3>

      <p className="text-sm text-white/60 mt-2 leading-relaxed">
        Understand the occupational and labour-market information
        supporting the platform.
      </p>
    </Link>

    <Link
      to="/faq"
      className="block rounded-xl border border-white/10 p-5 hover:border-[#EB6AB5] transition"
    >
      <h3 className="font-bold text-white">
        Frequently Asked Questions
      </h3>

      <p className="text-sm text-white/60 mt-2 leading-relaxed">
        Find answers about assessments, reports and career planning.
      </p>
    </Link>
  </div>
</section>
{/* ================= Author Profile ================= */}
{post.authors && (
  <section className="mt-12 rounded-3xl border border-[#EB6AB5]/25 bg-white/5 p-8">
    <p className="text-[#EB6AB5] font-bold uppercase tracking-widest text-sm">
      Written by
    </p>

    <div className="flex flex-col sm:flex-row items-start gap-5 mt-4">
      {post.authors.photo ? (
        <img
          src={post.authors.photo}
          alt={post.authors.name || "Article author"}
          className="w-20 h-20 rounded-full object-cover border border-[#EB6AB5]/40"
          width="80"
          height="80"
          loading="lazy"
        />
      ) : (
        <div className="w-20 h-20 shrink-0 rounded-full bg-[#EB6AB5] text-[#0F0A24] flex items-center justify-center text-3xl font-bold">
          {post.authors.name?.charAt(0)?.toUpperCase() || "R"}
        </div>
      )}

      <div className="flex-1">
        <h3 className="text-2xl font-bold">
          {post.authors.name}
        </h3>

        {post.authors.title && (
          <p className="text-[#EB6AB5] mt-1">
            {post.authors.title}
          </p>
        )}

        {post.authors.bio && (
          <p className="text-white/70 mt-4 leading-7">
            {post.authors.bio}
          </p>
        )}

        <div className="flex flex-wrap gap-5 mt-5">
          {post.authors.linkedin && (
            <a
              href={post.authors.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#EB6AB5] hover:underline"
            >
              LinkedIn
            </a>
          )}

          {post.authors.website && (
            <a
              href={post.authors.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#EB6AB5] hover:underline"
            >
              Website
            </a>
          )}
        </div>
      </div>
    </div>
  </section>
)}

<NewsletterSignup /> 
      </article>

      {relatedPosts.length > 0 && (
        <section className="max-w-5xl mx-auto mt-16 border-t border-white/10 pt-10">
          <p className="text-[#EB6AB5] font-bold uppercase tracking-widest text-sm">
            You may also like
          </p>

          <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-8">
            Related Articles
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {relatedPosts.map((item) => (
              <article
                key={item.id}
                className="rounded-2xl border border-[#EB6AB5]/20 bg-white/5 overflow-hidden hover:-translate-y-1 transition"
              >
                {item.cover_image_url && (
                  <img
                    src={item.cover_image_url}
                    alt={item.title}
                    className="w-full h-40 object-cover"
                  />
                )}

                <div className="p-5">
                  <p className="text-[#EB6AB5] text-xs font-bold uppercase tracking-widest mb-2">
                    {item.blog_categories?.name || "Career Strategy"}
                  </p>

                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>

                  <p className="text-white/60 text-sm mb-4 line-clamp-3">
                    {item.excerpt}
                  </p>

                  <Link
                    to={`/blog/${item.slug}`}
                    className="text-[#EB6AB5] font-semibold underline underline-offset-4"
                  >
                    Read More →
                  </Link>
                </div>
              </article>
              
            ))}
          </div>
        </section>
      )}

      <section className="max-w-4xl mx-auto mt-16 border-t border-white/10 pt-10">
  <h2 className="text-3xl font-bold mb-6">Comments</h2>

  {!session?.user ? (
  <div className="rounded-2xl border border-[#EB6AB5]/25 bg-white/5 p-6">
    <p className="text-white/70 mb-4">
      Please login to comment. New here? Create a free account to join the discussion.
    </p>

    <div className="flex flex-wrap gap-3">
      <Link
        to={`/login?return=/blog/${post.slug}`}
        className="px-6 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold"
      >
        Login to Comment
      </Link>
<button
  type="button"
  onClick={goToCommentRegister}
  className="px-6 py-3 rounded-full border border-[#EB6AB5]/40 text-[#EB6AB5] font-bold"
>
  Create Free Account
</button>
    </div>
  </div>
) : (
  <div className="mb-8">
    <div className="mb-4 rounded-xl bg-white/5 border border-white/10 p-4">
      <p className="text-white/70">
        Commenting as{" "}
        <span className="text-[#EB6AB5] font-semibold">
          {session.user.email}
        </span>
      </p>

      <div className="mt-4 flex flex-wrap gap-3">
        <Link
          to="/dashboard"
          className="px-4 py-2 rounded-full bg-white/10 text-white text-sm"
        >
          Open Free Calculator Dashboard
        </Link>

        <Link
          to="/questionnaire?plan=snapshot"
          onClick={() => trackBlogCta("start_snapshot")}
          className="px-4 py-2 rounded-full border border-[#EB6AB5]/40 text-[#EB6AB5] text-sm"
        >
          Start Snapshot Report
        </Link>

        <Link
          to="/questionnaire?plan=deep_audit"
          onClick={() => trackBlogCta("start_deep_audit")}
          className="px-4 py-2 rounded-full border border-[#EB6AB5]/40 text-[#EB6AB5] text-sm"
        >
          Start Deep Audit Report
        </Link>

        <button
          onClick={async () => {
            await supabase.auth.signOut();
            setSession(null);
            setComment("");
            setMessage("");
          }}
          className="px-4 py-2 rounded-full text-white/60 underline text-sm"
        >
          Logout
        </button>
      </div>
    </div>

    <textarea
      value={comment}
      onChange={(e) => setComment(e.target.value)}
      placeholder="Write your comment..."
      className="w-full min-h-[120px] rounded-2xl bg-white/5 border border-[#EB6AB5]/30 p-4 text-white outline-none"
    />

    <button
      onClick={submitComment}
      className="mt-4 px-6 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold"
    >
      Post Comment
    </button>

    {message && <p className="mt-3 text-sm text-[#EB6AB5]">{message}</p>}
  </div>
)}
  <div className="space-y-5">
    {comments.map((c) => (
      <div
        key={c.id}
        className="rounded-2xl bg-white/5 border border-white/10 p-5"
      >
        <p className="font-bold text-[#EB6AB5]">{c.user_name}</p>
        <p className="text-white/80 mt-2">{c.comment}</p>
      </div>
    ))}
  </div>
</section>    
      {authPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="relative w-full max-w-xl rounded-3xl border border-[#EB6AB5]/30 bg-[#171027] p-8 text-center shadow-2xl">
            <button
              type="button"
              onClick={() => setAuthPopup(false)}
              className="absolute right-5 top-4 text-white/60 hover:text-white text-2xl"
              aria-label="Close login popup"
            >
              ×
            </button>

            <h2 className="text-3xl font-extrabold mb-4">
              Login Required
            </h2>

            <p className="text-white/70 mb-7">
              Please login or create a free account to like, save, or
              interact with this article.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to={`/login?return=/blog/${post.slug}`}
                className="px-7 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold"
              >
                Login
              </Link>

              <button
                type="button"
                onClick={goToCommentRegister}
                className="px-7 py-3 rounded-full border border-[#EB6AB5]/40 text-[#EB6AB5] font-bold"
              >
                Create Free Account
              </button>
            </div>
          </div>
        </div>
      )}
    </main>

    <AppFooter
      variant="landing"
      logoSrc={logo}
    />
  </div>
);
}